import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import axios from "axios";
import { toInteger } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { post, put } from "~/Services";
import { passengerHost } from "~/Utils";
import { fields } from "../constants";
import { useHelpContentDocumentDetails } from "./PageContent.hooks";

export const AddEditPageContent = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const {
    data: editPageContent,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchCategories,
  } = useHelpContentDocumentDetails(query.get("editId") || "");

  const {
    data: editPageContentById,
    isLoading: isLoadingLocs,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchCategories();
    }
  }, [query.get("editId"), query.get("id")]);

  const [records, setRecords] = useState<any[]>([]);

  const getRecords = () => {
    let url = `${passengerHost}/api/get-help-cards-data-all?airport=${currentAirport}`;
    axios
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setRecords(envelopes);
        }
      })
      .catch(() => {
        setRecords([]);
      });
  };

  useEffect(() => {
    getRecords();
  }, []);

  const formConfig: FormConfig = {
    initialValues: {
      name: editPageContentById
        ? decodeHTML(editPageContentById.payload?.name)
        : editPageContent
          ? decodeHTML(editPageContent?.name)
          : "",
      section_english: editPageContentById
        ? decodeHTML(editPageContentById.payload?.section_english)
        : editPageContent
          ? decodeHTML(editPageContent?.section_english)
          : null,
      help_section_name_malay: editPageContentById
        ? decodeHTML(editPageContentById.payload?.help_section_name_malay)
        : editPageContent
          ? decodeHTML(editPageContent?.help_section_name_malay)
          : "",
      help_section_name_mandarin: editPageContentById
        ? decodeHTML(editPageContentById.payload?.help_section_name_mandarin)
        : editPageContent
          ? decodeHTML(editPageContent?.help_section_name_mandarin)
          : "",
      sequence: editPageContentById
        ? decodeHTML(editPageContentById.payload?.sequence)
        : editPageContent
          ? decodeHTML(editPageContent?.sequence)
          : "",
      help_redirect_link_english: editPageContentById
        ? decodeHTML(editPageContentById.payload?.help_redirect_link_english)
        : editPageContent
          ? decodeHTML(editPageContent?.help_redirect_link_english)
          : "",
      published: editPageContentById
        ? decodeHTML(editPageContentById.payload.published)
        : editPageContent
          ? decodeHTML(editPageContent?.published)
          : false,
    },
    // fields: fields.pageContent,
    fields: fields.pageContent.map((field: any) => {
      if (field.name === "sequence") {
        return {
          ...field,
          validation: {
            required: `${field.label || field.name} is required`,
            validate: (value: string, formValues: FieldValues) => {
              if (!value) return true;

              // If records not yet loaded, skip validation temporarily
              if (!records || records.length === 0) return true;

              const airportValue = currentAirport || formValues?.field_help_airports_en;
              const sectionValue = formValues?.section_english?.value || formValues?.section_english;

              if (!airportValue || !sectionValue) {
                return true; // Don’t validate until both are selected
              }

              const existingRecord = records.find((record: any) => {
                return (
                  toInteger(record.sequence) === toInteger(value) &&
                  record.field_help_airports_en?.toLowerCase() === airportValue?.toLowerCase() &&
                  record.section_english?.toLowerCase() === sectionValue?.toLowerCase() &&
                  record.id !== (editPageContent?.id || editPageContentById?.payload?.id)
                );
              });

              return !existingRecord || `Sequence already exists for Current Section and Airport`;
            },
          },
        };
      }
      return field;
    }),
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    console.log("data-123", data);

    let imageKeys: any = ["field_common_image"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "PASSENGER");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    const payload = {
      action,
      method,
      module: "Help",
      subModule: "pageContent",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [
        {
          appName: "PASSENGER_ECMS",
          apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && {
          id: editPageContent?.id || editPageContentById?.payload?.id,
        }),
        ...data,
        airports: currentAirport,
      },
      status,
    };

    console.log("Final Payload:", payload);

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post(`/management/publish`, payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Page Content`}
      showBack
    >
      <DynamicForm
        isLoading={drilldownLoading || isLoadingLocs}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") || query.get("type") === "update" ? `${passengerHost}/api/get-help-card-crud-api-data/${editPageContent?.id || editPageContentById?.payload?.id}` : `${passengerHost}/api/create-help-card-crud-api-data`}`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Help"
      />
    </ContentWrapper>
  );
};
